import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { useRxPanels } from "../../components/rx/useRxPanels";
import { RxRecordSheet } from "../../components/rx/RxRecordSheet";
import { NewPill } from "../../components/NewPill";
import { FileText, Info } from "lucide-react";

/** Prescription Record Sheet for one client, with create, duplicate, print, sign, void, renew, and pharmacy actions. */
export default function PrescriptionList({ client }) {
  const [items, setItems] = useState(null);
  const [failed, setFailed] = useState(false);
  const load = useCallback(() => api.get(`/clients/${client.id}/prescriptions`).then((r) => { setItems(r.data); setFailed(false); })
    .catch((e) => { setFailed(true); toast.error(apiError(e)); }), [client.id]);
  const { meta, me, openRx, newRx, editRx, panels } = useRxPanels(load);
  useEffect(() => { load(); }, [load]);
  if (failed && !items) return (
    <div className="rounded-xl border border-[#FFCDD2] bg-[#FFF5F5] p-4 text-sm text-[#B71C1C]" data-testid="rx-load-error">Prescriptions could not be loaded.
      <button type="button" data-testid="rx-load-retry" onClick={load} className="ml-2 font-semibold underline">Try again</button></div>
  );
  if (!meta || !items) return <div className="h-24 animate-pulse rounded-2xl bg-[#F5F2EB]" data-testid="rx-loading" />;

  return (
    <div className="space-y-4" data-testid="prescription-list">
      <div><h3 className="flex items-center gap-2 font-heading text-lg font-bold text-[#3E2723]"><FileText size={17} /> Prescription Record Sheet <NewPill feature="rx-record-sheet" /></h3>
        <p className="text-xs text-[#7B6157]">One row per medication. Tick rows to print, duplicate, or print the list. Double-click a row to open the prescription. Signed medications are added to the Master Medication List.</p></div>
      {!me.is_prescriber && <p className="flex items-center gap-1.5 rounded-xl bg-[#F5F2EB] px-3 py-2 text-xs text-[#6D4C41]" data-testid="rx-not-prescriber"><Info size={13} /> Only staff marked as prescribers can create, duplicate, or sign prescriptions. You can print, link pharmacies, discontinue, and log pharmacy communication.</p>}
      {me.is_prescriber && me.missing.length > 0 && <p className="rounded-xl border border-[#FFE0B2] bg-[#FFF8E1] px-3 py-2 text-xs text-[#8D5A00]" data-testid="rx-missing-creds">Your staff profile is missing {me.missing.join(" and ")}. An admin adds them in Settings, Users and Roles, before you can sign.</p>}
      <RxRecordSheet client={client} items={items} me={me} onNew={() => newRx(client)} onOpen={openRx} onDuplicated={editRx} onReload={load} />
      {panels}
    </div>
  );
}
